/**
 * What a credit is called on screen, per channel — and the one sentence about
 * what credits are *not*.
 *
 * THE SENTENCE MATTERS MORE THAN THE LABELS. A credit is Wend's own metering
 * unit, not money. Under bring-your-own-SES the tenant pays Amazon directly for
 * what actually happens on their own account, in amounts Wend never sees, and
 * under the Meta Cloud API they pay Meta per conversation. So nothing on the
 * admin top-up screen says "spend", "cost", "charge", "price" or "refund", and
 * no figure carries a currency symbol. The tenant's own credits screen already
 * holds that line; an admin screen that grants credits is where it would break
 * first, because "top up" is a phrase people meet at a payment page.
 *
 * The labels are the same two strings the tenant app uses (`CHANNEL_LABEL` in
 * `apps/web/src/lib/campaign-display.ts`). They are duplicated across the two
 * apps rather than shared, which is a real cost, and the reason it is paid is
 * that `@wend/shared` is contracts — the vocabulary the API and both front-ends
 * parse against — and a display string is not a contract. Moving it there would
 * make "WhatsApp" something the API validates.
 */
import { CHANNEL_VALUES, type Channel } from '@wend/shared';

/** Lower case, so it reads inside a sentence as well as above a figure. */
export const CHANNEL_LABEL: Readonly<Record<Channel, string>> = {
  email: 'email',
  whatsapp: 'WhatsApp',
};

/**
 * What one credit buys on that channel, for the caption under a balance.
 *
 * Per channel rather than one string, because they are not the same unit
 * upstream: Wend meters one credit per message on both, but Meta bills the
 * tenant per *conversation*. Saying "1 credit ≈ 1 message" on both is true of
 * Wend's metering and is the only claim this screen is entitled to make.
 */
export const CHANNEL_CREDIT_UNIT: Readonly<Record<Channel, string>> = {
  email: 'One credit is one email Wend sends.',
  whatsapp: 'One credit is one WhatsApp message Wend sends.',
};

/** Stable order for balance rows and the channel picker: the enum's own. */
export const CHANNELS: readonly Channel[] = CHANNEL_VALUES;
